/**
 * Formatação de data para leitura, sempre em `pt-BR` (at-66, frente D).
 *
 * 🔴 **Existe porque o mesmo formato estava escrito à mão em lugares diferentes**,
 * e um deles chegou a sair errado: a at-63 já tinha juntado **4 cópias** do fluxo
 * de vistoria numa `formatarDataVistoria` (uma delas chamava `toLocaleString()`
 * **sem locale**, então o formato saía conforme o navegador). A tela de
 * Acompanhamento de Oficina tinha a **quinta**, byte a byte igual, mais uma
 * variante com hora. Uma função só, um formato só — convenção da at-24.
 *
 * ⚠️ **Fora do design system, de propósito.** Isto é utilitário, não componente:
 * `src/ui` não conhece domínio nem regra de apresentação de dado. O precedente é
 * `utils/moeda.ts`, que nunca subiu ao kit.
 *
 * 📌 `formatarDataHora` tem **um** consumidor hoje (a oficina é a única tela em
 * que o dado envelhece dentro do próprio dia — o carimbo "consultado em" perderia
 * o sentido sem a hora). Nasce aqui mesmo assim porque é a **mesma família**: o
 * que se quer evitar é justamente uma segunda grafia de data solta numa view. A
 * regra de "primitivo sem consumidor plural não nasce" é do kit, não de `utils/`.
 */

/** O que se mostra quando não há data, ou quando ela não é legível. */
const SEM_DATA = '—';

/**
 * Converte, devolvendo `null` quando não dá para confiar no valor.
 *
 * ⚠️ `new Date(null)` é **época zero** (01/01/1970), não data inválida — por isso
 * a checagem de nulo vem antes do parse. Sem ela, um campo ausente viraria uma
 * data plausível na tela.
 */
const paraData = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? null : data;
};

/** `2026-09-08T15:56:00Z` → `08/09/2026`. Ausente ou inválida → `—`. */
export function formatarDataCurta(iso: string | null | undefined): string {
  const data = paraData(iso);
  return data ? data.toLocaleDateString('pt-BR') : SEM_DATA;
}

/**
 * `2026-09-08T15:56:00Z` → `08/09/2026 às 12:56`. Ausente ou inválida → `—`.
 *
 * Para dado que envelhece dentro do dia: um carimbo de "consultado em" só com a
 * data não distingue "há cinco minutos" de "hoje de manhã".
 */
export function formatarDataHora(iso: string | null | undefined): string {
  const data = paraData(iso);
  if (!data) return SEM_DATA;

  const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${data.toLocaleDateString('pt-BR')} às ${hora}`;
}
